import { useState } from 'react';
import { X, Clock, Repeat } from 'lucide-react';
import { api } from '../lib/api';
import { TIME_OF_DAY } from '../lib/data';
import { useI18n } from '../i18n';
import { toast } from 'sonner';

// Mon=0 .. Sun=6
const DAYS = [
    { idx: 0, en: 'Mon' },
    { idx: 1, en: 'Tue' },
    { idx: 2, en: 'Wed' },
    { idx: 3, en: 'Thu' },
    { idx: 4, en: 'Fri' },
    { idx: 5, en: 'Sat' },
    { idx: 6, en: 'Sun' },
];

const ICONS = ['🌿', '💧', '✂️', '🌱', '🍅', '🔍', '🧵', '🌻', '🥬', '🪏', '🧤', '☀️'];

function to24h(hour12, minute, ampm) {
    let h = parseInt(hour12, 10);
    if (isNaN(h)) return '';
    if (ampm === 'AM') h = h === 12 ? 0 : h;
    else h = h === 12 ? 12 : h + 12;
    return `${String(h).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

export default function AddTaskModal({ open, onClose, onAdded }) {
    const { t } = useI18n();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [time_of_day, setTime] = useState('Morning');
    const [priority, setPriority] = useState('medium');
    const [icon, setIcon] = useState('🌿');
    const [busy, setBusy] = useState(false);
    // Advanced scheduling
    const [useTime, setUseTime] = useState(false);
    const [hour12, setHour12] = useState('8');
    const [minute, setMinute] = useState('00');
    const [ampm, setAmpm] = useState('AM');
    const [repeatDays, setRepeatDays] = useState([]); // Mon=0..Sun=6
    const [alarmType, setAlarmType] = useState('notification');

    if (!open) return null;

    const toggleDay = (idx) => {
        setRepeatDays(prev => prev.includes(idx) ? prev.filter(d => d !== idx) : [...prev, idx].sort());
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!title.trim()) return;
        setBusy(true);
        try {
            const payload = { title, description, time_of_day, priority, icon };
            if (useTime) {
                payload.due_time = to24h(hour12, minute, ampm);
                payload.alarm_type = alarmType;
            }
            if (repeatDays.length > 0) payload.repeat_days = repeatDays;
            const r = await api.post('/tasks', payload);
            toast.success('Task added');
            setTitle(''); setDescription(''); setTime('Morning'); setPriority('medium'); setIcon('🌿');
            setUseTime(false); setRepeatDays([]); setHour12('8'); setMinute('00'); setAmpm('AM');
            onAdded?.(r.data);
            onClose?.();
        } catch {
            toast.error('Error');
        } finally { setBusy(false); }
    };

    return (
        <div data-testid="add-task-modal" className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-brand-ink/40 p-0 sm:p-4 backdrop-blur-sm">
            <div className="bg-brand-bg w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl border border-brand-border max-h-[92vh] overflow-y-auto">
                <div className="flex items-center justify-between px-5 py-4 border-b border-brand-border sticky top-0 bg-brand-bg z-10">
                    <h3 className="font-serif text-xl text-brand-ink inline-flex items-center gap-2">
                        <span aria-hidden="true">🪏</span> {t('task.add')}
                    </h3>
                    <button data-testid="btn-close-add-task" onClick={onClose} className="p-2 hover:bg-brand-bg2 rounded-full"><X size={18} /></button>
                </div>
                <form onSubmit={submit} className="p-5 space-y-4">
                    <label className="block">
                        <span className="text-xs uppercase tracking-widest font-semibold text-brand-ink3">{t('task.title')}</span>
                        <input data-testid="input-task-title" value={title} onChange={(e) => setTitle(e.target.value)} required className="mt-1 w-full bg-brand-card border border-brand-border rounded-2xl px-4 py-3" />
                    </label>
                    <label className="block">
                        <span className="text-xs uppercase tracking-widest font-semibold text-brand-ink3">{t('task.description')}</span>
                        <textarea data-testid="input-task-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows="2" className="mt-1 w-full bg-brand-card border border-brand-border rounded-2xl px-4 py-3 resize-none" />
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block">
                            <span className="text-xs uppercase tracking-widest font-semibold text-brand-ink3">{t('task.time')}</span>
                            <select data-testid="select-task-time" value={time_of_day} onChange={(e) => setTime(e.target.value)} className="mt-1 w-full bg-brand-card border border-brand-border rounded-2xl px-4 py-3">
                                {TIME_OF_DAY.map((x) => <option key={x}>{x}</option>)}
                            </select>
                        </label>
                        <label className="block">
                            <span className="text-xs uppercase tracking-widest font-semibold text-brand-ink3">{t('task.priority')}</span>
                            <select data-testid="select-task-prio" value={priority} onChange={(e) => setPriority(e.target.value)} className="mt-1 w-full bg-brand-card border border-brand-border rounded-2xl px-4 py-3">
                                <option value="low">low</option>
                                <option value="medium">medium</option>
                                <option value="high">high</option>
                            </select>
                        </label>
                    </div>

                    {/* Specific time + AM/PM */}
                    <div className="bg-brand-card border border-brand-border rounded-2xl p-3">
                        <label className="flex items-center justify-between cursor-pointer">
                            <span className="inline-flex items-center gap-2 text-sm font-medium text-brand-ink">
                                <Clock size={14} className="text-brand-green" /> Specific time
                            </span>
                            <input
                                data-testid="toggle-specific-time"
                                type="checkbox"
                                checked={useTime}
                                onChange={(e) => setUseTime(e.target.checked)}
                                className="h-4 w-4 accent-brand-green"
                            />
                        </label>
                        {useTime && (
                            <div className="mt-3 grid grid-cols-[1fr_1fr_auto] gap-2 items-center" data-testid="time-picker">
                                <select
                                    data-testid="select-hour"
                                    value={hour12}
                                    onChange={(e) => setHour12(e.target.value)}
                                    className="bg-brand-bg border border-brand-border rounded-xl px-3 py-2 text-center"
                                >
                                    {Array.from({ length: 12 }, (_, i) => i + 1).map(h => (
                                        <option key={h} value={String(h)}>{h}</option>
                                    ))}
                                </select>
                                <select
                                    data-testid="select-minute"
                                    value={minute}
                                    onChange={(e) => setMinute(e.target.value)}
                                    className="bg-brand-bg border border-brand-border rounded-xl px-3 py-2 text-center"
                                >
                                    {['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'].map(m => (
                                        <option key={m} value={m}>{m}</option>
                                    ))}
                                </select>
                                <div className="inline-flex rounded-xl border border-brand-border overflow-hidden">
                                    {['AM', 'PM'].map(p => (
                                        <button
                                            key={p}
                                            type="button"
                                            data-testid={`btn-ampm-${p.toLowerCase()}`}
                                            onClick={() => setAmpm(p)}
                                            className={`px-3 py-2 text-xs font-semibold ${ampm === p ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-ink'}`}
                                        >
                                            {p}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Repeat on specific days */}
                    <div className="bg-brand-card border border-brand-border rounded-2xl p-3">
                        <div className="flex items-center justify-between mb-2">
                            <span className="inline-flex items-center gap-2 text-sm font-medium text-brand-ink">
                                <Repeat size={14} className="text-brand-green" /> Repeat on
                            </span>
                            {repeatDays.length > 0 && (
                                <button
                                    type="button"
                                    onClick={() => setRepeatDays([])}
                                    className="text-xs text-brand-ink3 hover:text-brand-ink underline"
                                    data-testid="btn-clear-repeat"
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-1.5" data-testid="repeat-days">
                            {DAYS.map(d => (
                                <button
                                    key={d.idx}
                                    type="button"
                                    data-testid={`btn-day-${d.en.toLowerCase()}`}
                                    onClick={() => toggleDay(d.idx)}
                                    className={`min-w-[40px] h-9 rounded-full text-xs font-semibold border transition-colors ${repeatDays.includes(d.idx) ? 'bg-brand-green text-white border-brand-green' : 'bg-brand-bg text-brand-ink border-brand-border hover:bg-brand-bg2'}`}
                                >
                                    {d.en}
                                </button>
                            ))}
                        </div>
                        {repeatDays.length === 0 && (
                            <p className="text-[11px] text-brand-ink3 mt-2">One-off task (no repeat)</p>
                        )}
                    </div>

                    {/* Alarm type only when time set */}
                    {useTime && (
                        <label className="block">
                            <span className="text-xs uppercase tracking-widest font-semibold text-brand-ink3">Reminder</span>
                            <select
                                data-testid="select-alarm-type"
                                value={alarmType}
                                onChange={(e) => setAlarmType(e.target.value)}
                                className="mt-1 w-full bg-brand-card border border-brand-border rounded-2xl px-4 py-3"
                            >
                                <option value="notification">Push notification</option>
                                <option value="alarm">Alarm (loud)</option>
                                <option value="none">No reminder</option>
                            </select>
                        </label>
                    )}

                    <label className="block">
                        <span className="text-xs uppercase tracking-widest font-semibold text-brand-ink3">Icon</span>
                        <div className="mt-1 flex flex-wrap gap-1.5">
                            {ICONS.map((em) => (
                                <button type="button" key={em} onClick={() => setIcon(em)} data-testid={`btn-icon-${em}`} className={`text-xl w-10 h-10 rounded-xl border ${icon === em ? 'bg-brand-green border-brand-green' : 'bg-brand-card border-brand-border'}`}>{em}</button>
                            ))}
                        </div>
                    </label>
                    <button data-testid="btn-submit-task" disabled={busy} type="submit" className="w-full bg-brand-green hover:bg-brand-greenDark text-white py-3 rounded-full font-medium disabled:opacity-50">
                        {busy ? 'Saving…' : t('common.save')}
                    </button>
                </form>
            </div>
        </div>
    );
}
